import $ from "jquery";
import * as utils from "./utils.ts";
import { isLocalhost, formatFileName, getEnvironment } from "./utils.ts";
import axios from "axios";
import { buildListUrl, buildSelectQuery } from "./SpUtility.ts";
import { matterProperties } from "./ListProperties.ts";
import { IMatter, IUser } from "@/types/SPTypes.tsx";

export const get = async (url: string): Promise<any> => {
  const finalUrl = isLocalhost() ? "_proxy" + url : url;

  try {
    const response = await axios.get(encodeURI(finalUrl), {
      headers: {
        Accept: "application/json;odata=verbose"
      }
    });

    const data = response.data?.d;
    return data?.results ?? [data];
  } catch (error: any) {
    const message = error?.response?.data || error.message || "Unknown error";
    throw message;
  }
};

export const update = async (url: string, payload: any): Promise<any> => {
  const finalUrl = isLocalhost() ? "_proxy" + url : url;

  try {
    const response = await axios.post(
      encodeURI(finalUrl),
      JSON.stringify(payload),
      {
        headers: {
          Accept: "application/json;odata=verbose",
          "Content-Type": "application/json;odata=verbose",
          "IF-MATCH": "*",
          "X-HTTP-Method": "MERGE",
          "X-RequestDigest": ($("#__REQUESTDIGEST").val() as string) || ""
        }
      }
    );

    return response.data;
  } catch (error: any) {
    throw error?.response?.data || error.message || "Unknown error";
  }
};

export const save = async (url: string, payload: any): Promise<any> => {
  const finalUrl = isLocalhost() ? "_proxy" + url : url;

  try {
    const response = await axios.post(
      encodeURI(finalUrl),
      JSON.stringify(payload),
      {
        headers: {
          "Content-Type": "application/json;odata=verbose",
          Accept: "application/json;odata=verbose",
          "X-RequestDigest": ($("#__REQUESTDIGEST").val() as string) || ""
        }
      }
    );

    return response.data?.d;
  } catch (error: any) {
    throw error?.response?.data || error.message || "Unknown error";
  }
};

// Ska testas i logp miljön:
export function updateFormDigest(): Promise<void> {
  return new Promise(async (resolve, reject) => {
    const env = utils.getEnvironmentPaths();
    const url = env.baseUrl + "/_api/contextinfo";

    try {
      const response = await axios.post(url, null, {
        headers: {
          Accept: "application/json;odata=verbose"
        }
      });

      console.log("current Form Digest value: ", $("#__REQUESTDIGEST").val());

      const newDigest =
        response.data?.d?.GetContextWebInformation?.FormDigestValue;

      if (newDigest) {
        $("#__REQUESTDIGEST").val(newDigest);
        console.log("new Form Digest value: ", $("#__REQUESTDIGEST").val());
      }

      resolve();
    } catch (error: any) {
      reject(
        error?.response?.data ||
          error.message ||
          "Failed to update form digest."
      );
    }
  });
}

export const saveFile = async (
  libraryName = "MatterFiles",
  files: File[],
  belongsToId?: number,
  vsdlId?: number
) => {
  const env = getEnvironment();

  try {
    const saveFilePromises = files.map(async file => {
      const fileName = formatFileName(file.name);
      const saveFileUrl = `/tjansterprodukter/${env}/_api/lists/getByTitle('${libraryName}')/RootFolder/Files/add(url='${fileName}',overwrite='true')?$expand=ListItemAllFields`;

      const saveFileResponse = await axios({
        method: "post",
        url: !isLocalhost() ? saveFileUrl : "_proxy" + saveFileUrl,
        data: file,
        headers: {
          Accept: "application/json;odata=verbose",
          "X-RequestDigest": ($("#__REQUESTDIGEST").val() as string) || "",
          "Content-Type": "text/plain; charset=UTF-8"
        }
      });

      let dataObject;
      if (libraryName === "MatterFiles") {
        dataObject = {
          __metadata: {
            type: `SP.Data.${libraryName}Item`
          },
          Title: saveFileResponse.data.d.Name,
          belongsToMatterId: belongsToId
        };
      }
      if (libraryName === "FAQFiles") {
        dataObject = {
          __metadata: {
            type: `SP.Data.${libraryName}Item`
          },
          Title: saveFileResponse.data.d.Name,
          // Only include these if we actually parsed numbers
          ...(typeof belongsToId === "number"
            ? { belongsToFAQ: belongsToId }
            : {}),
          ...(typeof vsdlId === "number" ? { vsdlId } : {})
        };
      }

      const updatedItemUrl = `/tjansterprodukter/${env}/_api/web/lists/getByTitle('${libraryName}')/items(${saveFileResponse.data.d.ListItemAllFields.Id})`;

      await axios({
        method: "post",
        url: !isLocalhost() ? updatedItemUrl : "_proxy" + updatedItemUrl,
        data: dataObject,
        headers: {
          Accept: "application/json;odata=verbose",
          "X-RequestDigest": ($("#__REQUESTDIGEST").val() as string) || "",
          "X-HTTP-Method": "MERGE",
          "IF-MATCH": "*",
          "Content-Type": "application/json;odata=verbose"
        }
      });
    });

    await Promise.all(saveFilePromises);

    return true;
  } catch (error) {
    console.error("Error saving files:", error);
    return false;
  }
};

export const getMattersForCurrentUser = async (
  currentUser: IUser
): Promise<IMatter[]> => {
  const formatUsername = utils.userTitleToFullName(currentUser.Title);
  const query =
    buildListUrl("Matters") +
    buildSelectQuery(matterProperties) +
    `&$filter=clientName eq '${formatUsername}'`;

  try {
    const response = await get(query);
    return response;
  } catch (error) {
    console.error("Error fetching client matters: ", error);
    throw error;
  }
};

export const getMattersForCurrentUserWithComments = async (
  matterId: number
) => {
  const query =
    buildListUrl("MatterEvents") +
    `?` +
    `$filter=belongsToMatterId eq ${matterId}`;
  try {
    const commentsResponse = await get(query);
    return commentsResponse;
  } catch (err) {
    console.error("Error fetching comments: ", err);
    throw err;
  }
};
